import React, { useMemo } from 'react';
import { Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { useVarLimitOverviewQuery } from '../../../../api/rs-hg/risk/hooks/queries';
import type {
  VarLimitFundRow,
  VarLimitOverviewPageModel,
} from '../../../../api/rs-hg/risk/varLimitOverview.types';
import { RSPageFund } from '../../../../api/api.types';
import PageLinkButton from '../../../buttons/PageLinkButton';
import RaptorStatusBox from '../../../feedback/RaptorStatusBox.component';
import { GeneralOverviewPageRenderedComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import GridItem from '../../../layout/GridComponents/GridItem';
import CustomTable from '../../../tables/CustomTable';
import { CustomColumn } from '../../../../types/components/tables/tableTypes';
import { Status } from '../../../../types/redux/data/dataTypes';
import FundNameWithDemoPill from '../../../ui/FundNameWithDemoPill';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import { useDemoFundIds } from '../../../../hooks/useDemoFundIds';
import { raptorPlatformIdSelector } from '../../../../redux/raptorPlatform/selectors';
import { sortFundNames, statusSort } from '../../../../utilities/customSorting';
import { demoRowStyle } from '../../../../utilities/demoFundUtils';
import { specifyStatus } from '../../../../utilities/generalMappings';
import { percentageToTwoDecimalPlaces } from '../../../../utilities/numberFormatters';
import { regulationBucketKeyFromFundType } from '../../dashboard/dashboard-utils';

interface VarLimitTableRow {
  internalFundName: string;
  fundName: string;
  varValue: number | null;
  varLimit: number | null;
  fundVar: number | null;
  benchmarkVar: number | null;
  status: Status;
  section: string | null;
  isDemoFund: boolean;
  ucitsDefaultLimits: boolean;
  error?: string;
}

const centered = {
  cellStyle: { textAlign: 'center' as const },
  headerStyle: { textAlign: 'center' as const },
};

function compareNullableNumber(a: number | null, b: number | null): number {
  if (a == null && b == null) return 0;
  if (a == null) return 1;
  if (b == null) return -1;
  return a - b;
}

function buildTableRows(
  page: VarLimitOverviewPageModel,
  demoFundIds: Set<string>,
): VarLimitTableRow[] {
  const fundById = new Map(
    (page.funds ?? []).map((fund) => [fund.fund_id.toLowerCase(), fund]),
  );

  return page.data
    .map((fund) => toTableRow(fund, fundById, demoFundIds))
    .sort((a, b) =>
      sortFundNames(
        a.internalFundName || a.fundName,
        b.internalFundName || b.fundName,
      ),
    );
}

function toTableRow(
  fund: VarLimitFundRow,
  fundById: Map<string, RSPageFund>,
  demoFundIds: Set<string>,
): VarLimitTableRow {
  const pageFund = fundById.get(fund.fund_id.toLowerCase());
  const internalFundName = fund.fund_name || pageFund?.fund_name || '';

  return {
    internalFundName,
    fundName:
      pageFund?.fund_name_full ||
      fund.fund_name_external ||
      internalFundName ||
      fund.fund_id,
    varValue: fund.latest?.var ?? null,
    varLimit: fund.var_limit ?? null,
    fundVar: fund.latest?.var_fund ?? null,
    benchmarkVar: fund.latest?.var_reference ?? null,
    status: specifyStatus(fund.latest?.status || ''),
    section: regulationBucketKeyFromFundType(pageFund?.fund_type),
    isDemoFund:
      Boolean(pageFund?.is_demo_fund) || demoFundIds.has(internalFundName),
    ucitsDefaultLimits: Boolean(fund.ucits_default_limits),
    error: fund.error,
  };
}

function percentColumn(
  title: string,
  field: 'varValue' | 'varLimit' | 'fundVar' | 'benchmarkVar',
): CustomColumn<VarLimitTableRow> {
  return {
    title,
    field,
    ...centered,
    customSort: (a, b) => compareNullableNumber(a[field], b[field]),
    render: (row) =>
      row[field] == null ? '-' : percentageToTwoDecimalPlaces(row[field]),
  };
}

function buildColumns(showRelative: boolean): CustomColumn<VarLimitTableRow>[] {
  const valueColumns: CustomColumn<VarLimitTableRow>[] = [
    percentColumn('VaR', 'varValue'),
    ...(showRelative
      ? [
        percentColumn('Fund VaR', 'fundVar'),
        percentColumn('Benchmark VaR', 'benchmarkVar'),
      ]
      : []),
    {
      title: 'VaR Limit',
      field: 'varLimit',
      ...centered,
      customSort: (a, b) => compareNullableNumber(a.varLimit, b.varLimit),
      render: (row) => (
        <span>
          {row.varLimit == null
            ? '-'
            : percentageToTwoDecimalPlaces(row.varLimit)}
          <Typography
            component="span"
            variant="caption"
            color="textSecondary"
            display="block"
            style={{ fontSize: '0.8rem', lineHeight: 1.35 }}
          >
            {row.ucitsDefaultLimits ? 'UCITS default' : 'Preference'}
          </Typography>
        </span>
      ),
    },
  ];

  return [
    {
      title: 'Fund ID',
      field: 'internalFundName',
      width: '12rem',
      cellStyle: { whiteSpace: 'nowrap' },
      headerStyle: { whiteSpace: 'nowrap' },
      customSort: (a, b) =>
        sortFundNames(a.internalFundName, b.internalFundName),
      render: (row) => (
        <FundNameWithDemoPill
          isDemo={row.isDemoFund}
          className="flex-wrap gap-y-1"
        >
          {row.internalFundName || '-'}
        </FundNameWithDemoPill>
      ),
    },
    {
      title: 'Fund Name',
      field: 'fundName',
      width: '32%',
      customSort: (a, b) => sortFundNames(a.fundName, b.fundName),
    },
    ...valueColumns,
    {
      title: 'Status',
      field: 'status',
      width: '12.50rem',
      pdfRenderType: 'PageLinkButton',
      ...centered,
      customSort: (a, b) => statusSort(a, b, 'status'),
      render: (row) =>
        row.internalFundName && row.section ? (
          <PageLinkButton
            status={row.status}
            fundName={row.internalFundName}
            page="value-at-risk"
            section={row.section}
            tooltipText={row.error}
          />
        ) : (
          <RaptorStatusBox status={row.status} tooltipValue={row.error} />
        ),
    },
  ];
}

const VarLimitOverview: React.FC<GeneralOverviewPageRenderedComponentProps> = ({
  positionDate,
  section,
  pageId,
}) => {
  const raptorPlatformId = useSelector(raptorPlatformIdSelector);
  const demoFundIds = useDemoFundIds(section, pageId);
  const overviewQuery = useVarLimitOverviewQuery(
    positionDate,
    raptorPlatformId,
  );

  const rows = useMemo(() => {
    const page = overviewQuery.data;
    if (!page || !Array.isArray(page.data) || !page.data.length) return [];
    return buildTableRows(page, demoFundIds);
  }, [overviewQuery.data, demoFundIds]);

  const showRelative = useMemo(
    () => rows.some((row) => row.fundVar != null || row.benchmarkVar != null),
    [rows],
  );

  const columns = useMemo(() => buildColumns(showRelative), [showRelative]);

  const tableTitle = (
    <div>
      VaR Limit Overview
      <Typography
        variant="caption"
        color="textSecondary"
        display="block"
        style={{
          fontWeight: 400,
          fontSize: '0.85rem',
          lineHeight: 1.6,
          marginTop: 4,
        }}
      >
        Preference: the fund's prospectus VaR limit.
        <br />
        <span style={{ whiteSpace: 'nowrap' }}>
          UCITS default: used when the fund has no prospectus limit. Absolute
          VaR 20% (alert at 18%). Relative VaR 200% (alert at 180%).
        </span>
      </Typography>
    </div>
  );

  return (
    <QueryBoundary<VarLimitOverviewPageModel>
      isLoading={overviewQuery.isLoading}
      isFetching={overviewQuery.isFetching}
      isError={overviewQuery.isError}
      error={overviewQuery.error}
      data={rows.length ? overviewQuery.data : null}
      loadingMessages={['Loading VaR limit data...']}
      errorMessage="Unable to load VaR limit data."
      noDataMessage="No VaR limit data available."
    >
      {() => (
        <GridItem xs={12} card>
          <CustomTable<VarLimitTableRow>
            columns={columns}
            data={rows}
            showToolbar
            title="VaR Limit Overview"
            id="var_limit_overview_table"
            options={{
              paging: false,
              search: true,
              exportButton: true,
              tableLayout: 'fixed',
              rowStyle: demoRowStyle<VarLimitTableRow>(),
            }}
            toolbarComponents={{
              toolbarTitle: tableTitle,
            }}
          />
        </GridItem>
      )}
    </QueryBoundary>
  );
};

export default VarLimitOverview;
